<template>
  <main class="learning-app" @click.capture="recordClick">
    <header class="lesson-header"><span class="lesson-mark" aria-hidden="true">GF</span><div><p class="eyebrow">Read · Listen · Discover</p><h1>{{ lesson.title }}</h1></div></header>
    <p v-if="mode === 'demo'" class="demo-banner" role="note">Practice preview · Some pictures and sounds are not added yet. Your practice stays on this device.</p>
    <form v-if="!checkpoint" class="entry-form" @submit.prevent="start">
      <h2 tabindex="-1">Ready to learn?</h2><p>Enter the participant ID your teacher gave you.</p>
      <label for="learning-participant">Participant ID</label>
      <input id="learning-participant" v-model="participantId" maxlength="80" autocomplete="off" required :disabled="busy" />
      <p class="muted">Use the same ID on this browser to continue your saved work. Do not enter your name.</p>
      <button type="submit" :disabled="busy || !participantId.trim()">{{ busy ? 'Opening…' : 'Start or continue' }}</button>
    </form>
    <template v-else>
      <StudyProgress :index="checkpoint.progress.stage_index" :total="lesson.stages.length" :title="stage.title" :completed="checkpoint.status === 'completed'" />
      <section v-if="checkpoint.status === 'completed'" class="completion-panel"><span aria-hidden="true" class="completion-star">★</span><h2 tabindex="-1">You did it!</h2><p>Thank you for learning with us.</p><p>{{ mode === 'demo' ? 'Your practice has been saved on this device.' : 'All your work has been saved.' }}</p></section>
      <section v-else class="activity-panel" :aria-busy="busy">
        <h2 tabindex="-1">{{ stage.title }}</h2><p class="activity-instructions">{{ stage.instructions }}</p>
        <fieldset class="activity-body" :disabled="disabled">
          <legend class="sr-only">{{ stage.title }}</legend>
          <template v-if="stage.type === 'introduction'">
            <div class="character-grid"><MediaPlaceholder v-for="material in stage.materials" :key="material.id" :material="material" /></div>
            <label for="prediction">{{ stage.prompt }}</label><textarea id="prediction" :value="state.prediction" rows="2" maxlength="1000" @change="perform({ type: 'prediction', value: $event.target.value })" />
          </template>
          <component :is="activityComponent" v-else :key="stage.id" :stage="stage" :state="state" :disabled="disabled" @action="perform" />
        </fieldset>
        <div class="stage-actions">
          <button :disabled="disabled || !ready" @click="perform({ type: 'stage_complete' })">{{ stage.type === 'introduction' ? "LET’S START!" : isLast ? 'Save and finish' : 'Save and continue →' }}</button>
          <button v-if="mode === 'demo' && !ready" class="secondary" :disabled="disabled" @click="perform({ type: 'stage_complete', skipped: true })">Skip this preview activity →</button>
        </div>
        <p class="muted">You can change your work here. After continuing, this activity is finished.</p>
      </section>
    </template>
    <p v-if="busy" role="status" class="save-status">Saving your work…</p>
    <div v-if="error" role="alert" class="error"><p>{{ error }}</p><button v-if="pending" :disabled="busy" @click="retry">Try saving again</button><button v-if="conflict" class="secondary" :disabled="busy" @click="loadSaved">Use the latest saved progress</button></div>
    <p v-else-if="checkpoint && !busy" role="status" class="save-status">{{ mode === 'demo' ? 'Practice saved on this device' : 'Your work is saved' }}</p>
  </main>
</template>
<script>
import { markRaw } from 'vue';
import { openLearningSession, learningError } from '../../services/lessonRepository';
import { newCheckpoint } from '../../learning/sessionModel';
import { stageReady } from '../../learning/sessionModel';
import { makeOperation } from '../../learning/eventLog';
import StudyProgress from './StudyProgress.vue';
import MediaPlaceholder from './MediaPlaceholder.vue';
import PhonicsTask from './PhonicsTask.vue';
import WordBuilder from './WordBuilder.vue';
import SoundWordMatching from './SoundWordMatching.vue';
import StoryReader from './StoryReader.vue';
import PackBagTask from './PackBagTask.vue';
import SortingTask from './SortingTask.vue';
import SequenceTask from './SequenceTask.vue';

const activityComponents = { phonics: PhonicsTask, 'word-builder': WordBuilder, matching: SoundWordMatching, reader: StoryReader, 'pack-bag': PackBagTask, sorting: SortingTask, sequence: SequenceTask };
export default {
  components: { StudyProgress, MediaPlaceholder },
  props: { lesson: { type: Object, required: true }, mode: { type: String, required: true } },
  data: () => ({ participantId: '', checkpoint: null, repository: null, busy: false, error: '', conflict: false, pending: null, clicks: [], droppedClicks: 0, mediaEvents: [], lastTick: null, timer: null }),
  computed: {
    stage() { return this.lesson.stages[this.checkpoint.progress.stage_index]; },
    state() { return (this.pending?.checkpoint.progress.stage_id === this.stage.id ? this.pending.checkpoint : this.checkpoint).stage_states[this.stage.id]; },
    ready() { return stageReady(this.stage, this.state); },
    disabled() { return this.busy || Boolean(this.pending); },
    isLast() { return this.checkpoint.progress.stage_index === this.lesson.stages.length - 1; },
    activityComponent() { return markRaw(activityComponents[this.stage.type]); }
  },
  mounted() {
    this.timer = window.setInterval(() => this.heartbeat(), 15000);
    window.addEventListener('beforeunload', this.beforeUnload);
    document.addEventListener('visibilitychange', this.visibilityChanged);
  },
  beforeUnmount() {
    window.clearInterval(this.timer);
    window.removeEventListener('beforeunload', this.beforeUnload);
    document.removeEventListener('visibilitychange', this.visibilityChanged);
  },
  methods: {
    async start() {
      if (this.busy || !this.participantId.trim()) return;
      this.busy = true; this.error = ''; this.conflict = false;
      try {
        const repository = await openLearningSession(this.lesson, this.participantId.trim(), this.mode);
        this.repository = markRaw(repository);
        this.checkpoint = repository.checkpoint || newCheckpoint(this.lesson, this.participantId.trim(), repository.owner, this.mode);
        this.lastTick = Date.now();
        this.pending = repository.pending;
        this.busy = false;
        if (this.pending) await this.retry();
        else if (this.checkpoint.status !== 'completed') await this.perform({ type: repository.checkpoint ? 'session_resume' : 'session_start' });
        this.focusHeading();
      } catch (error) { this.error = learningError(error); }
      finally { this.busy = false; }
    },
    async perform(action) {
      if (action.type.startsWith('audio_')) {
        if (this.checkpoint && this.checkpoint.status !== 'completed') {
          this.mediaEvents.push({ ...action, stage_id: action.stage_id || this.stage.id, at: new Date().toISOString(), session_ms: this.checkpoint.total_active_ms + Date.now() - this.lastTick });
          if (!this.disabled) this.heartbeat();
        }
        return;
      }
      if (!this.checkpoint || this.disabled || this.checkpoint.status === 'completed') return;
      this.busy = true; this.error = '';
      try {
        const now = Date.now();
        const operation = makeOperation(this.checkpoint, this.lesson, action, now - this.lastTick, this.clicks, this.droppedClicks, now, this.mediaEvents);
        // Persist the exact operation BEFORE awaiting the network. Refresh retries it idempotently.
        this.repository.stagePending(operation);
        this.pending = operation;
        this.clicks = []; this.droppedClicks = 0; this.mediaEvents = [];
        await this.savePending();
      } catch (error) { this.conflict = ['session/conflict', 'session/completed'].includes(error.code); this.error = learningError(error); }
      finally { this.busy = false; }
    },
    async savePending() {
      const previousStage = this.checkpoint.progress.stage_id;
      this.checkpoint = await this.repository.commit(this.pending);
      this.pending = null; this.conflict = false; this.error = ''; this.lastTick = Date.now();
      if (previousStage !== this.checkpoint.progress.stage_id || this.checkpoint.status === 'completed') this.focusHeading();
    },
    async retry() {
      if (!this.pending || this.busy) return;
      this.busy = true;
      try { await this.savePending(); }
      catch (error) { this.conflict = ['session/conflict', 'session/completed'].includes(error.code); this.error = learningError(error); }
      finally { this.busy = false; }
    },
    async loadSaved() {
      if (this.busy) return;
      try { this.repository.clearPending(); this.pending = null; this.checkpoint = null; this.clicks = []; this.droppedClicks = 0; await this.start(); }
      catch (error) { this.error = learningError(error); }
    },
    focusHeading() { this.$nextTick(() => this.$el.querySelector('section h2, form h2')?.focus()); },
    recordClick(event) {
      if (!this.checkpoint || this.disabled || this.checkpoint.status === 'completed') return;
      if (this.clicks.length >= 100) { this.droppedClicks++; return; }
      this.clicks.push({ x: event.clientX, y: event.clientY, target: event.target.tagName, at: new Date().toISOString(), session_ms: this.checkpoint.total_active_ms + Date.now() - this.lastTick });
    },
    heartbeat() { if (this.checkpoint && !this.disabled && this.checkpoint.status !== 'completed') this.perform({ type: 'heartbeat' }); },
    visibilityChanged() { if (this.checkpoint && !this.disabled && this.checkpoint.status !== 'completed') this.perform({ type: 'visibility', hidden: document.hidden }); },
    beforeUnload(event) {
      if (this.checkpoint?.status === 'in_progress') { this.heartbeat(); event.preventDefault(); event.returnValue = ''; }
    }
  }
};
</script>
